/**
 * KarroFin Admin — endpoint map.
 *
 * Kept separate from src/app-js/urls.js on purpose: that file is shared legacy
 * code and this module should not need to touch it.
 *
 * Every path below is an endpoint that ALREADY EXISTS in cashpo-server. None of
 * them are called while USE_MOCK is true -- they are recorded here so the
 * swap from mock to live is a one-line change inside each api/ function
 * rather than an archaeology exercise.
 *
 * Paths are relative to VUE_APP_BASE_URL, which already ends in "/api/".
 */
const KARROFIN_URLS = {
  /* ------------------------------------------------------------- leads */
  // webpanel/urls.py -> LeadsListView
  LEADS_LIST: "web-panel/leads_list/",
  // webpanel/urls.py -> CustomerListView / CustomerCountView (both POST)
  CUSTOMER_LIST: "web-panel/customer_list/",
  // Every offer one customer has, across all their loans -- the popup
  // behind User Management's "4 / 4 clicked" figure.
  CUSTOMER_OFFERS: "web-panel/customer_offers/",
  // The four stage buckets above User Management. Same body as CUSTOMER_LIST.
  USER_STAGE_COUNTS: "web-panel/karrofin_user_stage_counts/",
  CUSTOMER_EXPORT: "web-panel/customer_export/",
  CUSTOMER_COUNT: "web-panel/customer_count/",

  /* ------------------------------------------------------ applications */
  // webpanel/urls.py -> PreCheckFastView / PrecheckDetailsCountsFastView
  PRECHECK_LIST: "web-panel/precheck_details_fast/",
  PRECHECK_EXPORT: "web-panel/precheck_export/",
  DISBURSEMENT_EXPORT: "web-panel/disbursement_export/",
  // reports/urls.py -> MISReportIssuesAPIView (CSV; append "<report id>/")
  MIS_REPORT_ISSUES: "reports/mis/issues/",
  // reports/urls.py -> lender-reported leads on the Leads screen (MIS-<id>).
  // Append "<id>/history/", "<id>/candidates/" or "<id>/match/".
  REPORT_LEADS: "reports/report-leads/",
  // Every MIS row behind one offer / one tracked click (CC<id>). Append "<id>/".
  OFFER_REPORT_HISTORY: "reports/offer-history/",
  CLICK_REPORT_HISTORY: "reports/click-history/",
  // The pipeline buttons above Offer Management. Same query string as
  // PRECHECK_LIST. The list's own `allcount` is a stub of zeros -- not this.
  PRECHECK_STAGE_COUNTS: "web-panel/precheck_stage_counts/",
  PRECHECK_COUNTS: "web-panel/precheck_details_counts_fast/",
  // webpanel/urls.py -> GetFunnelView
  FUNNEL: "web-panel/admin/get_user_track_record/",

  /* --------------------------------------------------------- dashboard */
  // webpanel/urls.py -> KarrofinDashboardView. Everything the Dashboard draws
  // in one response: KPI tiles, the six conversion ratios, the funnel, the
  // clicks/disbursals trend, recent clicks and the product tab counts.
  KARROFIN_DASHBOARD: "web-panel/karrofin_dashboard/",

  /* -------------------------------------------------------- disbursals */
  // webpanel/urls.py -> DisbursementListView (returns per-product allcount)
  DISBURSEMENT_LIST: "web-panel/disbursement/list/",

  /* ------------------------------------------------ offers / products */
  // products/urls.py -- the offer catalogue
  /* The create/update payloads need loan-type ids, not the display labels
     the form works in, so the screen resolves one to the other. */
  LOAN_TYPE_LIST: "master/get_loan_type/",
  PRODUCT_LIST: "product/admin/product_list/",
  PRODUCT_EXPORT: "product/admin/product_export/",
  PRODUCT_CREATE: "product/admin/product_create/",
  PRODUCT_UPDATE: "product/admin/product_update/", // + <id>/
  PRODUCT_MOVE: "product/admin/product_move/",
  ELIGIBILITY_LIST: "product/admin/eligibility_list/",
  ELIGIBILITY_CREATE: "product/admin/eligibility_create/",
  ELIGIBILITY_UPDATE: "product/admin/eligibility_update/", // + <id>/
  /* The pincode sheet is a bulk edit of one product's serviceable-pincode
     list: an .xlsx in, the whole list replaced. Not under product/admin/ --
     it predates that grouping. */
  PRODUCT_PINCODE_SHEET: "product/upload_pincode_sheet/",
  PINCODE_SERVICEABILITY: "product/pincode_serviceability/",
  // The disbursal / status write endpoint for an individual offer
  OFFER_CHANGE_STATUS: "product/change_status/", // + <id>/
  OFFER_STATUS_LIST: "product/offer_status_list/",

  /* ------------------------------------------------------- campaign URLs */
  // products/urls.py -> AdminCampaignCreateAPIView / AdminCampaignListAPIView.
  // The create call returns the finished link: the marketing site's address is
  // a server setting (CAMPAIGN_BASE_URL) and the token can only be signed
  // server-side, so the browser never assembles the URL itself.
  CAMPAIGN_CREATE: "product/admin/campaign_create/",
  CAMPAIGN_LIST: "product/admin/campaign_list/",
  // Edit an existing campaign: name, products, on/off. `campaign_id` is NOT in
  // the payload -- it is signed into the token, so the link a campaign was
  // shared under keeps resolving to it.
  CAMPAIGN_UPDATE: "product/admin/campaign_update/", // + <id>/
  // The off switch for a link already in circulation -- the row and the URL
  // survive, the public page says "no longer active". Delete is the other,
  // unrecoverable action.
  CAMPAIGN_STATUS: "product/admin/campaign_status/", // + <id>/
  CAMPAIGN_DELETE: "product/admin/campaign_delete/", // + <id>/
  // Clicks through a campaign's dynamic link and the signups they became,
  // grouped by partner_id / publisher / source, for an optional date range.
  CAMPAIGN_STATS: "product/admin/campaign_stats/", // + <id>/
  // PUBLIC -- what the marketing site's /campaign/:token page calls. Recorded
  // here for reference; this panel never calls it.
  CAMPAIGN_PRODUCTS: "product/campaign/product_list/",

  /* ----------------------------------------------------------- filters */
  // Live this is composed from several legacy lookups (master/city_list/,
  // users/lead_partner/list/, master/get_count_dropdown/, the team list);
  // recorded here as one name so the screen asks one question.
  FILTER_OPTIONS: "web-panel/precheck_filter_options/",

  /* --------------------------------------------------------------- utm */
  // users/urls.py -> GetUtmDropdown (distinct values for each utm_* column)
  UTM_DROPDOWN: "users/get_utm_dropdown/",

  /* ----------------------------------------------------- notifications */
  // notification/urls.py
  FCM_TOPIC: "notification/fcm_topic/",
  FCM_NOTIFICATION: "notification/fcm_notification/",
  NOTIFICATION_HISTORY: "notification/get_notification_history/",
  NOTIFICATION_GRAPH: "notification/get_graph_report_data/",
  NOTIFICATION_USER_LIST: "notification/get_user_list/",
  // The three the KarroFin panel actually reads. Distinct from the two above:
  // get_notification_history/ is campaign-level (bulk vs individual, no channel
  // and no delivery status) and get_user_list/ is a POST returning a paginated
  // customer list, neither of which this screen can be built from.
  KF_NOTIFICATION_HISTORY: "notification/karrofin_notification_history/",
  KF_NOTIFICATION_AUDIENCE: "notification/karrofin_notification_audience/",
  KF_NOTIFICATION_TEMPLATES: "notification/karrofin_notification_templates/",

  // The send. Also KarroFin's own, for the same reason as the three above: the
  // two legacy send endpoints below are push-only and take an FCMNotification
  // template id, so neither can express "this channel, this title, this body".
  KF_SEND_NOTIFICATION: "notification/karrofin_send_notification/",
  // Customer search behind Compose's Individual mode. Not the customer list:
  // this one answers reachability and allowlist status for a given channel,
  // which is what the picker has to show before a name can be chosen.
  KF_NOTIFICATION_RECIPIENTS: "notification/karrofin_notification_recipients/",
  // The Automation screen's Notification History: every dry-run proposal and
  // real automation/agent send, with the customer's status at trigger time.
  // Read-only -- opening it sends nothing.
  KF_AUTOMATION_HISTORY: "notification/automation_history/",
  // Distinct titles across that history, for its multi-select Title filter.
  KF_AUTOMATION_HISTORY_TITLES: "notification/automation_history/titles/",
  // Its User history tab: one row per borrower who received a message.
  KF_AUTOMATION_HISTORY_USERS: "notification/automation_history/users/",
  // The Agent Tracker (notification/growth/reports.py): every borrower the
  // growth agent dealt with and, per borrower, each day's message and why.
  // Read-only. The server's day-based plan / skipped calls and its moves /
  // performance / transitions / coverage calls are no longer shown.
  // Every borrower the agent planned for or looked at, newest first, and one
  // borrower's days (message, one-line what happened, why, status).
  KF_GROWTH_USERS: "notification/growth/users/",
  KF_GROWTH_USER_HISTORY: "notification/growth/users/history/",
  // Missing templates: borrowers no approved message fitted, by type and level.
  KF_GROWTH_GAPS: "notification/growth/gaps/",
  KF_GROWTH_GAPS_SUGGEST: "notification/growth/gaps/suggest/",
  // NOTE: unused by this panel -- push-only, and superseded by KF_SEND_NOTIFICATION.

  SEND_ON_TOPIC: "notification/send_notification_on_topic/",
  SEND_INDIVIDUALLY: "notification/send_notification_individually/",

  /* -------------------------------------------------- template library */
  // notification/urls.py -> the [IsPanelUser] half. See
  // cashpo-server/docs/template-library-api.md.
  //
  // Distinct from KF_NOTIFICATION_TEMPLATES above, which answers "what names
  // will the provider accept on this channel" for the Compose screen. These are
  // the approved COPY the agent selects from -- the whole library, with its
  // review workflow. The agent's own two endpoints (templates/available/ and
  // agent/send/) are NOT here: they take an internal API key, not a user token,
  // and nothing in the panel may call them.
  TEMPLATE_LIST: "notification/templates/",
  TEMPLATE_CREATE: "notification/templates/",
  TEMPLATE_BULK: "notification/templates/bulk/",
  // + "<id>/"
  TEMPLATE_DETAIL: "notification/templates/",

  /* ----------------------------------------------------- business loan */
  // businessLoan/urls.py
  BL_LOAN_LIST: "businessLoan/admin/get_loan_list/",

  /* --------------------------------------------------------- insurance */
  // products/urls.py. The Insurance screen runs on mock data (the dev3
  // database has no insurance rows at all), so these are the live path that
  // takes over the moment `insurance: true` is added to LIVE_MODULES.
  USER_INSURANCE_LIST: "product/insurance/user_insurance_list/",
  INSURANCE_PRODUCT_LIST: "product/insurance/product_list/",
  INSURANCE_UPDATE_STATUS: "product/insurance/update_status/",
};

export default KARROFIN_URLS;
